import React, { useEffect } from 'react';

import { FlowContextProvider, StepWrapper, useExitToPath, useQuery, when } from '@betterment/js-runtime';
import { CenteredWorkflowLayout, WorkflowHeader } from '@betterment/style-closet-react';
import { Route, Routes, useMatch, useNavigate } from 'react-router-dom';

import {
  DirectDepositConfig,
  DirectDepositFlowValues,
  DirectDepositProps,
  DirectDepositSteps,
  PayCheckAmount,
} from './data';
import { EmployerName } from './EmployerName';
import { InitialForm } from './InitialForm';
import { Overview } from './Overview';
import { PayCheckAmountField } from './PayCheckAmountField';
import { PayCheckPercentageField } from './PayCheckPercentageField';
import { SelectPayCheckAmountType } from './SelectPayCheckAmountType';
import { DirectDepositFlowTitleProvider, useTitleState } from './utils';

import FullPageLoader from '../../../Shared/components/FullPageLoader';
import { locationAssign } from '../../../Shared/path-helpers';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { useAccount } from '../AccountProvider';

const defaultPaycheck: PayCheckAmount = { type: 'percentage', value: 100 };

export interface ControllerProps extends DirectDepositProps {
  initialStep?: DirectDepositSteps;
}

const Header: React.FC<React.PropsWithChildren<{ exitToPath: string }>> = ({ exitToPath }) => {
  const navigateRouter = useNavigate();
  const title = useTitleState();
  return (
    <WorkflowHeader
      backButtonHandler={() => navigateRouter(-1)}
      title={title}
      closeHandler={() => locationAssign(exitToPath)}
    />
  );
};

export const Controller: React.FC<React.PropsWithChildren<ControllerProps>> = ({
  employerName,
  payCheckAmount = defaultPaycheck,
  initialStep,
}) => {
  const { account } = useAccount();
  const match = useMatch('/checking/accounts/:accountId/direct_deposit/*');

  const defaultPath = urlBuilder.getAppCheckingSummary.absolute({ accountId: account.id });

  const exitToPath = useExitToPath(defaultPath);
  const defaultStep = initialStep || 'overview';

  const {
    loading: isConfigLoading,
    data: directDepositConfig,
    refetch: requestDirectDepositConfig,
  } = useQuery<DirectDepositConfig>(urlBuilder.apiV2CheckingDirectDepositConfig.absolute({ accountId: account.id }), {
    skip: true,
  });

  useEffect(() => {
    if (!employerName) {
      void requestDirectDepositConfig();
    }
  }, [requestDirectDepositConfig, employerName]);

  const isLoading = !employerName && isConfigLoading;

  const contentOrLoader = () => {
    if (isLoading) return <FullPageLoader />;

    return (
      <FlowContextProvider
        defaultStep={defaultStep}
        returnToPath={exitToPath}
        initialValues={{
          employerName: employerName || directDepositConfig?.employerName,
          payCheckAmount,
        }}
        basePath={match!.pathnameBase}
        routePath=""
      >
        <Routes>
          <Route
            data-feature="checking_account_direct_deposit"
            path="/overview"
            element={
              <StepWrapper<DirectDepositFlowValues> name="overview">
                {stepProps => <Overview {...stepProps} account={account} />}
              </StepWrapper>
            }
          />
          <Route
            data-feature="checking_account_direct_deposit"
            path="/initialForm"
            element={
              <StepWrapper<DirectDepositFlowValues>
                name="initialForm"
                next={[
                  [
                    'selectingPayCheckType',
                    when<DirectDepositFlowValues>((_, { values }) => values.lastUserEvent === 'selectedEditingDeposit'),
                  ],
                  [
                    'editingEmployer',
                    when<DirectDepositFlowValues>(
                      (_, { values }) => values.lastUserEvent === 'selectedEditingEmployer'
                    ),
                  ],
                ]}
              >
                {stepProps => <InitialForm {...stepProps} />}
              </StepWrapper>
            }
          />
          <Route
            data-feature="checking_account_direct_deposit"
            path="/selectingPayCheckType"
            element={
              <StepWrapper<DirectDepositFlowValues>
                name="selectingPayCheckType"
                next={[
                  ['initialForm', when((_, { values }) => values.lastUserEvent === 'selectedEntirePayCheck')],
                  [
                    'editingPayCheckDollarAmount',
                    when((_, { values }) => values.lastUserEvent === 'selectedPayCheckDollarAmount'),
                  ],
                  [
                    'editingPayCheckPercentage',
                    when((_, { values }) => values.lastUserEvent === 'selectedPayCheckPercentage'),
                  ],
                ]}
              >
                {stepProps => <SelectPayCheckAmountType {...stepProps} />}
              </StepWrapper>
            }
          />
          <Route
            data-feature="checking_account_direct_deposit"
            path="/editingEmployer"
            element={
              <StepWrapper<DirectDepositFlowValues> name="editingEmployer" next="initialForm">
                {stepProps => <EmployerName {...stepProps} />}
              </StepWrapper>
            }
          />
          <Route
            data-feature="checking_account_direct_deposit"
            path="/editingPayCheckPercentage"
            element={
              <StepWrapper<DirectDepositFlowValues> name="editingPayCheckPercentage" next="initialForm">
                {stepProps => <PayCheckPercentageField {...stepProps} />}
              </StepWrapper>
            }
          />
          <Route
            data-feature="checking_account_direct_deposit"
            path="/editingPayCheckDollarAmount"
            element={
              <StepWrapper<DirectDepositFlowValues> name="editingPayCheckDollarAmount" next="initialForm">
                {stepProps => <PayCheckAmountField {...stepProps} />}
              </StepWrapper>
            }
          />
        </Routes>
      </FlowContextProvider>
    );
  };

  return (
    <DirectDepositFlowTitleProvider>
      <CenteredWorkflowLayout>
        <CenteredWorkflowLayout.Slot name="header">
          <Header exitToPath={exitToPath} />
        </CenteredWorkflowLayout.Slot>

        <CenteredWorkflowLayout.Slot name="content">{contentOrLoader()}</CenteredWorkflowLayout.Slot>
      </CenteredWorkflowLayout>
    </DirectDepositFlowTitleProvider>
  );
};
